/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { useQuery, useMutation } from 'react-query';
import { getAllResearchFields } from 'apis/projectProposals.api';
import { getAll as getAllProposalPrograms } from 'apis/proposalPrograms';
import { getProposalById, updateProposal, uploadProposalFile } from 'apis/proposals';
import { toast } from 'layout';
import { TOAST } from 'constant';
import Loading from 'components/Loading';
import { getShortMissionType, toNonAccentVietnamese } from 'utils/func';
import ProposalForm from '../../components/Form';
import { proposalValidationSchema } from '../../validations';

const formOptions = { resolver: yupResolver(proposalValidationSchema) };

export default function EditProposalPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { id } = useParams();

  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [selectedProgramId, setSelectedProgramId] = useState();
  const [formData, setFormData] = useState(null);
  const [action, setAction] = useState('save');

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    setValue,
    reset,
    watch,
  } = useForm({ ...formOptions, mode: 'onChange' });

  const {
    data: proposalDetailData,
    refetch,
    isLoading,
  } = useQuery(['proposalDetail', id], () => getProposalById(id), {
    enabled: !!id,
  });

  const { data: researchFieldsData } = useQuery(
    ['projectProposalResearchFields'],
    getAllResearchFields
  );

  const { data: programsData } = useQuery(['proposalPrograms'], getAllProposalPrograms);

  const researchFields = useMemo(
    () =>
      researchFieldsData?.data.map((e) => ({
        id: e.id,
        name: e.code ? t(`researchFields.${e.code}`) : e.name,
        code: e.code,
      })) ?? [],
    [researchFieldsData, t]
  );

  const programs = useMemo(
    () =>
      programsData?.data.map((e) => ({
        id: e.id,
        name: e.name,
        code: e.code,
      })) ?? [],
    [programsData]
  );

  useEffect(() => {
    if (proposalDetailData?.data) {
      const data = proposalDetailData?.data;
      setSelectedProgramId(data.proposalProgramId);
      const owner = {
        ownerId: data?.ownerId,
        email: data?.owner?.email,
        contact: {
          email: data?.owner?.email,
        },
        fullname: data?.owner?.fullname,
        gender: data?.owner?.scientificProfile?.gender,
        academicRank: data?.owner?.scientificProfile?.academicRank,
        academicDegree: data?.owner?.scientificProfile?.academicDegree,
        organizationId: data?.owner?.organizationId,
        specificOrganizationName: data?.owner?.specificOrganizationName,
      };
      reset({
        vietnameseName: data.vietnameseName,
        description: data.description,
        yearProposed: data.yearProposed,
        target: data.target,
        type: data.type,
        projectType: data.projectType,
        researchFieldIds: data.proposalResearchFields.map(
          ({ researchField, researchFieldId }) => researchField?.id ?? researchFieldId
        ),
        file: data.attachmentFileUrl,
        owner,
      });
    }
  }, [proposalDetailData, setValue]);

  const { mutateAsync: updateProposalMutateAsync } = useMutation(updateProposal);

  const { mutateAsync: uploadMutateAsync } = useMutation(uploadProposalFile);

  const handleSubmitProposal = async (paramData) => {
    const dataToUse = { ...formData, ...paramData };
    const { owner, ...restData } = dataToUse;
    const finalData = {
      ...restData,
      ownerId: owner?.ownerId,
      id,
      proposalResearchFields: dataToUse.researchFieldIds.map((researchFieldId) => ({
        researchFieldId,
      })),
      ...(selectedProgramId && { proposalProgramId: selectedProgramId }),
      ...(action === 'submit' && { status: 'submitted' }),
    };

    // Check if there are any dirty fields except for the file field
    if (
      Object.keys(dirtyFields).filter((key) => key !== 'file').length > 0 ||
      action === 'submit'
    ) {
      try {
        await updateProposalMutateAsync(finalData);
        setIsSubmitModalOpen(false);

        if (action === 'submit') {
          toast(TOAST.SUCCESS, t('proposal.create.successSubmitMessage'));
        } else {
          toast(TOAST.SUCCESS, t('proposal.create.successEditMessage'));
        }
      } catch (error) {
        if (error?.response?.data?.code === 'proposalAlreadyExists') {
          toast(TOAST.ERROR, t('proposal.create.proposalAlreadyExists'));
        }
        if (error?.response?.data?.code === 'proposalProgramNotInTimeRange') {
          toast(TOAST.ERROR, t('proposal.create.proposalProgramNotInTimeRange'));
        }
        if (error?.response?.data?.code === 'scientificProfileNotFound') {
          toast(TOAST.WARN, t('proposal.create.scientificProfileNotFound'));
        }
      }
    }

    // Handle file upload if a new file is selected
    if (
      dirtyFields.file &&
      dataToUse?.file &&
      dataToUse?.file instanceof File &&
      action === 'save'
    ) {
      const formFileData = new FormData();
      formFileData.append('proposalId', proposalDetailData?.data?.id);

      // eslint-disable-next-line no-unused-vars
      const newFileName = `R01-DX-${getShortMissionType(finalData?.type)}-${
        finalData.yearProposed
      }-${toNonAccentVietnamese(owner.fullname)}.pdf`;

      const renamedFile = new File([dataToUse?.file], newFileName, {
        type: dataToUse?.file.type,
      });

      formFileData.append('proposalFile', renamedFile);

      try {
        await uploadMutateAsync(formFileData);
        toast(TOAST.SUCCESS, t('proposal.create.successEditMessage'));
      } catch (error) {
        toast(TOAST.ERROR, t('proposal.create.errorUploadFile'));
      }
    }

    reset();
    refetch();
  };

  const onSubmit = (data) => {
    setFormData(data);
    if (action === 'submit') {
      setIsSubmitModalOpen(true);
    } else {
      handleSubmitProposal(data);
    }
  };

  return (
    <>
      {isLoading && <Loading />}
      <div style={{ padding: '45.5px' }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ProposalForm
            disabled={proposalDetailData?.data?.status === 'submitted'}
            control={control}
            errors={errors}
            researchFields={researchFields}
            proposalDetailData={proposalDetailData?.data}
            watch={watch}
            setValue={setValue}
          />
          <div
            className="text-right mt-4"
            style={{ display: 'flex', justifyContent: 'flex-end', gap: '14px' }}
          >
            <Button
              label={t('proposal.create.cancel')}
              className="w-7rem"
              severity="danger"
              onClick={() => navigate('/proposal')}
            />
            {proposalDetailData?.data?.status === 'new' && (
              <>
                <Button
                  label={t('proposal.create.save')}
                  className="w-7rem"
                  severity="info"
                  type="submit"
                  onClick={() => {
                    setAction('save');
                  }}
                  disabled={!isDirty || Object.keys(dirtyFields).length === 0}
                />
                <Button
                  label={t('proposal.create.submit')}
                  className="w-7rem"
                  severity="success"
                  onClick={() => {
                    setAction('submit');
                  }}
                  type="submit"
                  disabled={isDirty && Object.keys(dirtyFields).length > 0}
                />
              </>
            )}
          </div>
        </form>

        <Dialog
          header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
          visible={isSubmitModalOpen}
          position="center"
          style={{ width: '400px' }}
          onHide={() => setIsSubmitModalOpen(false)}
          draggable={false}
        >
          <label htmlFor="programId" className="inline-block mb-2">
            {t('projectProposal.review.program')}{' '}
            <span className="text-red-500" style={{ fontWeight: 900 }}>
              *
            </span>
          </label>

          <Dropdown
            id="proposalProgramId"
            value={selectedProgramId}
            options={programs}
            onChange={(e) => setSelectedProgramId(e.value)}
            optionLabel="name"
            optionValue="id"
            showClear
            placeholder={t('proposal.create.selectProgram')}
            style={{ width: '100%' }}
          />

          <div
            className="text-right mt-4"
            style={{ display: 'flex', justifyContent: 'flex-end', gap: '14px' }}
          >
            <Button
              label={t('proposal.create.cancel')}
              className="w-7rem"
              severity="danger"
              onClick={() => setIsSubmitModalOpen(false)}
            />
            <Button
              label={t('proposal.create.ok')}
              className="w-7rem"
              severity="success"
              onClick={async () => {
                await handleSubmitProposal();
              }}
              disabled={!selectedProgramId}
            />
          </div>
        </Dialog>
      </div>
    </>
  );
}
